import { useEffect, useState } from "react";
/* eslint-disable react/no-unescaped-entities */
import { Link, useNavigate } from "react-router-dom";
import { Pencil, Plus, Trash2, LogOut, FileText, Settings, Sparkles, LayoutGrid, RefreshCw, Loader2, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DEFAULT_CONTENT } from "@/lib/content";
import { SECTION_SCHEMAS } from "./sectionSchemas";

// Editable content sections — labels driven by schema definitions
const SECTIONS = Object.entries(SECTION_SCHEMAS).map(([key, s]) => ({
    key,
    label: s.label,
    description: s.description,
}));

const TABS = [
    { id: "sections", label: "Contenu du site", Icon: LayoutGrid },
    { id: "articles", label: "Articles", Icon: FileText },
    { id: "social", label: "IA Réseaux sociaux", Icon: Sparkles },
];

function AdminShell({ children }) {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-xv-ivory">
            <header className="bg-xv-midnightDeep text-xv-ivory">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                    <Link to="/admin" className="flex items-center gap-4">
                        <img
                            src="/brand/logo-principal.png"
                            alt="XV"
                            className="h-10 w-auto"
                        />
                        <span className="overline text-xv-goldSoft hidden md:inline">
                            Espace administrateur
                        </span>
                    </Link>
                    <div className="flex items-center gap-6">
                        <span className="hidden md:inline text-[11px] tracking-[0.24em] uppercase text-xv-ivory/60">
                            {user?.email}
                        </span>
                        <Link
                            to="/"
                            className="text-[11px] tracking-[0.24em] uppercase text-xv-ivory/80 hover:text-xv-ivory"
                        >
                            Voir le site
                        </Link>
                        <button
                            data-testid="admin-logout"
                            onClick={() => {
                                logout();
                                navigate("/admin/login");
                            }}
                            className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-xv-goldSoft hover:text-xv-gold"
                        >
                            <LogOut strokeWidth={1.5} size={14} /> Déconnexion
                        </button>
                    </div>
                </div>
            </header>
            <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
                {children}
            </main>
        </div>
    );
}

export default function AdminDashboard() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [tab, setTab] = useState(() => {
        if (typeof window === "undefined") return "sections";
        return localStorage.getItem("xv_admin_tab") || "sections";
    });

    useEffect(() => {
        if (typeof window !== "undefined") {
            localStorage.setItem("xv_admin_tab", tab);
        }
    }, [tab]);

    const loadArticles = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/articles");
            setArticles(data);
        } catch {
            setArticles([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadArticles();
    }, []);

    const deleteArticle = async (id) => {
        if (!window.confirm("Supprimer cet article ?")) return;
        try {
            await api.delete(`/admin/articles/${id}`);
            setMessage("Article supprimé");
            await loadArticles();
            setTimeout(() => setMessage(null), 2500);
        } catch (e) {
            setMessage(e?.response?.data?.detail || "Erreur lors de la suppression");
        }
    };

    return (
        <AdminShell>
            <div data-testid="admin-dashboard" className="space-y-10">
                {/* TABS NAVIGATION */}
                <nav
                    data-testid="admin-tabs"
                    className="border-b border-xv-line flex flex-wrap gap-x-2 gap-y-1"
                >
                    {TABS.map(({ id, label, Icon }) => (
                        <button
                            key={id}
                            type="button"
                            data-testid={`admin-tab-${id}`}
                            onClick={() => setTab(id)}
                            className={`inline-flex items-center gap-3 px-6 py-4 text-[11px] tracking-[0.28em] uppercase transition-colors duration-300 border-b-2 -mb-px ${
                                tab === id
                                    ? "text-xv-midnight border-xv-gold"
                                    : "text-xv-text-secondary/70 border-transparent hover:text-xv-midnight"
                            }`}
                        >
                            <Icon strokeWidth={1.4} size={14} />
                            {label}
                        </button>
                    ))}
                </nav>

                {message && (
                    <div className="bg-xv-cream border-l-4 border-xv-gold p-4 text-xv-midnight font-light text-sm">
                        {message}
                    </div>
                )}

                {tab === "sections" && <SectionsTab />}
                {tab === "articles" && (
                    <ArticlesTab
                        articles={articles}
                        loading={loading}
                        onDelete={deleteArticle}
                        onReload={loadArticles}
                    />
                )}
                {tab === "social" && <SocialAITab />}
            </div>
        </AdminShell>
    );
}

// ─────────────────────────────────────────────────────────────
// SectionsTab — édition du contenu du site
// ─────────────────────────────────────────────────────────────

function SectionsTab() {
    return (
        <section>
            <div className="mb-10">
                <div className="overline text-xv-gold mb-2">
                    <Settings
                        strokeWidth={1.5}
                        size={14}
                        className="inline-block mr-2 align-middle -mt-1"
                    />
                    Contenu éditorial
                </div>
                <h2
                    className="font-serif text-4xl md:text-5xl text-xv-black"
                    style={{ fontWeight: 400 }}
                >
                    Sections du site
                </h2>
                <p className="mt-3 text-xv-text-secondary font-light text-[15px] max-w-2xl">
                    Modifiez chaque section structurée du site. Les
                    modifications sont publiées immédiatement.
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-px bg-xv-line border border-xv-line">
                {SECTIONS.map((s) => (
                    <Link
                        key={s.key}
                        to={`/admin/content/${s.key}`}
                        data-testid={`admin-section-${s.key}`}
                        className="bg-xv-ivory hover:bg-xv-cream transition p-8 group"
                    >
                        <div className="overline text-xv-gold mb-3">
                            Section · {s.key}
                        </div>
                        <h3
                            className="font-serif text-2xl text-xv-black group-hover:text-xv-midnight transition leading-tight"
                            style={{ fontWeight: 400 }}
                        >
                            {s.label}
                        </h3>
                        {s.description && (
                            <p className="mt-3 text-[13px] text-xv-text-secondary/85 font-light leading-relaxed line-clamp-2">
                                {s.description}
                            </p>
                        )}
                        <div className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-xv-midnight">
                            Éditer
                            <span className="block w-4 h-px bg-xv-midnight group-hover:w-8 transition-all" />
                        </div>
                    </Link>
                ))}
            </div>

            <p className="mt-6 text-xs text-xv-text-secondary font-light italic">
                Astuce : chaque section dispose d'un formulaire dédié avec
                champs nommés et upload d'image intégré.
            </p>
        </section>
    );
}

// ─────────────────────────────────────────────────────────────
// ArticlesTab — gestion des articles
// ─────────────────────────────────────────────────────────────

function ArticlesTab({ articles, loading, onDelete, onReload }) {
    const [activeJob, setActiveJob] = useState(null);
    const [busy, setBusy] = useState(null); // slug of article being enriched

    // On mount, restore any in-progress job so the progress bar
    // reappears when the admin leaves and comes back to the tab.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/articles/jobs");
                const items = data.items || [];
                const running = items.find((j) =>
                    j.status === "queued" || j.status === "running",
                );
                if (running) setActiveJob(running);
            } catch (e) {
                // non-critical
            }
        })();
    }, []);

    // Poll active job every 3 seconds
    useEffect(() => {
        if (!activeJob || ["done", "failed"].includes(activeJob.status)) return;
        const iv = setInterval(async () => {
            try {
                const { data } = await api.get(`/admin/articles/jobs/${activeJob.id}`);
                setActiveJob(data);
                if (["done", "failed"].includes(data.status)) {
                    clearInterval(iv);
                    onReload?.();
                }
            } catch (e) {
                // poll-tolerant
            }
        }, 3000);
        return () => clearInterval(iv);
    }, [activeJob?.id, activeJob?.status]);

    const startBatch = async () => {
        if (!window.confirm(
            "Enrichir les 14 articles avec l'IA ?\n\n"
            + "Cette opération peut prendre 5-10 minutes.\n"
            + "Le contenu original est toujours sauvegardé — vous pourrez restaurer."
        )) return;
        try {
            const { data } = await api.post("/admin/articles/enrich-all");
            setActiveJob(data);
        } catch (e) {
            alert(e?.response?.data?.detail || "Impossible de démarrer le batch");
        }
    };

    const enrichOne = async (slug) => {
        setBusy(slug);
        try {
            const { data } = await api.post(`/admin/articles/${slug}/enrich`);
            setActiveJob(data);
        } catch (e) {
            alert(e?.response?.data?.detail || "Enrichissement impossible");
        } finally {
            setBusy(null);
        }
    };

    const revertOne = async (slug) => {
        if (!window.confirm("Restaurer la version originale de cet article ?")) return;
        try {
            await api.post(`/admin/articles/${slug}/revert`);
            onReload?.();
        } catch (e) {
            alert(e?.response?.data?.detail || "Restauration impossible");
        }
    };

    return (
        <section>
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="overline text-xv-gold mb-2">
                        <FileText
                            strokeWidth={1.5}
                            size={14}
                            className="inline-block mr-2 align-middle -mt-1"
                        />
                        Articles
                    </div>
                    <h2
                        className="font-serif text-4xl md:text-5xl text-xv-black"
                        style={{ fontWeight: 400 }}
                    >
                        Notes d'analyses
                    </h2>
                </div>
                <div className="flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={startBatch}
                        disabled={!!activeJob && !["done", "failed"].includes(activeJob.status)}
                        data-testid="admin-enrich-all"
                        className="inline-flex items-center gap-3 px-6 py-3 border-2 border-xv-gold text-xv-gold text-[11px] tracking-[0.28em] uppercase hover:bg-xv-gold hover:text-xv-black transition disabled:opacity-40"
                    >
                        <Wand2 strokeWidth={1.5} size={14} />
                        Enrichir tous les articles avec l'IA
                    </button>
                    <Link
                        to="/admin/articles/new"
                        data-testid="admin-new-article"
                        className="inline-flex items-center gap-3 px-6 py-3 bg-xv-midnight text-xv-ivory text-[11px] tracking-[0.28em] uppercase hover:bg-xv-black transition"
                    >
                        <Plus strokeWidth={1.5} size={14} /> Nouvel article
                    </Link>
                </div>
            </div>

            {activeJob && <JobProgress job={activeJob} />}

            {loading ? (
                <p className="text-xv-text-secondary font-light">
                    Chargement…
                </p>
            ) : (
                <div className="border-t border-xv-line">
                    {articles.length === 0 && (
                        <p className="py-12 text-center text-xv-text-secondary font-light">
                            Aucun article. Créez le premier.
                        </p>
                    )}
                    {articles.map((a) => (
                        <div
                            key={a.id}
                            data-testid={`admin-article-${a.slug}`}
                            className="grid md:grid-cols-12 gap-4 items-center py-6 border-b border-xv-line"
                        >
                            <div className="md:col-span-1 overline text-xv-gold">
                                {a.published ? "Publié" : "Brouillon"}
                            </div>
                            <div className="md:col-span-6">
                                <h3
                                    className="font-serif text-2xl text-xv-black"
                                    style={{ fontWeight: 400 }}
                                >
                                    {a.title}
                                </h3>
                                <p className="text-[13px] text-xv-text-secondary font-light mt-1">
                                    /{a.slug} · {a.kicker}
                                    {a.enriched_at && a.read_time_minutes ? (
                                        <span className="ml-2 text-xv-gold">
                                            · Enrichi ({a.read_time_minutes} min)
                                        </span>
                                    ) : null}
                                </p>
                            </div>
                            <div className="md:col-span-5 flex items-center justify-end gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={() => enrichOne(a.slug)}
                                    disabled={busy === a.slug || (!!activeJob && !["done", "failed"].includes(activeJob.status))}
                                    data-testid={`admin-enrich-${a.slug}`}
                                    className="inline-flex items-center gap-2 px-3 py-2 border border-xv-gold/60 text-xv-gold text-[10px] tracking-[0.24em] uppercase hover:bg-xv-gold hover:text-xv-black transition disabled:opacity-40"
                                >
                                    {busy === a.slug ? (
                                        <Loader2 strokeWidth={1.5} size={12} className="animate-spin" />
                                    ) : (
                                        <Wand2 strokeWidth={1.5} size={12} />
                                    )}
                                    {a.enriched_at ? "Ré-enrichir" : "Enrichir"}
                                </button>
                                {a.enriched_at && (
                                    <button
                                        type="button"
                                        onClick={() => revertOne(a.slug)}
                                        data-testid={`admin-revert-${a.slug}`}
                                        className="inline-flex items-center gap-2 px-3 py-2 border border-xv-line text-xv-text-secondary text-[10px] tracking-[0.24em] uppercase hover:border-xv-black hover:text-xv-black transition"
                                    >
                                        <RefreshCw strokeWidth={1.5} size={12} /> Restaurer
                                    </button>
                                )}
                                <Link
                                    to={`/admin/articles/${a.id}`}
                                    data-testid={`admin-edit-${a.slug}`}
                                    className="inline-flex items-center gap-2 px-3 py-2 border border-xv-midnight text-xv-midnight text-[10px] tracking-[0.24em] uppercase hover:bg-xv-midnight hover:text-xv-ivory transition"
                                >
                                    <Pencil strokeWidth={1.5} size={12} /> Modifier
                                </Link>
                                <button
                                    onClick={() => onDelete(a.id)}
                                    data-testid={`admin-delete-${a.slug}`}
                                    className="inline-flex items-center gap-2 px-3 py-2 border border-red-700/40 text-red-700 text-[10px] tracking-[0.24em] uppercase hover:bg-red-700 hover:text-xv-ivory transition"
                                >
                                    <Trash2 strokeWidth={1.5} size={12} /> Supprimer
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

function JobProgress({ job }) {
    const done = job.status === "done";
    const failed = job.status === "failed";
    const pct = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;
    return (
        <div
            data-testid="admin-enrich-job"
            className={`mb-10 border p-6 ${
                done
                    ? "border-xv-gold bg-xv-gold/[0.04]"
                    : failed
                      ? "border-red-700/40 bg-red-50"
                      : "border-xv-midnight bg-xv-midnight/[0.04]"
            }`}
        >
            <div className="flex items-baseline justify-between mb-3 gap-4 flex-wrap">
                <div className="overline text-xv-gold flex items-center gap-2">
                    {!done && !failed && (
                        <Loader2 strokeWidth={1.5} size={12} className="animate-spin" />
                    )}
                    {job.kind === "batch" ? "Enrichissement global" : "Enrichissement en cours"}
                    {" · "}
                    {done ? "Terminé" : failed ? "Échec" : job.status}
                </div>
                <span className="text-[11px] tracking-[0.28em] uppercase text-xv-text-secondary">
                    {job.processed}/{job.total} · {pct} %
                </span>
            </div>
            <div className="h-[3px] bg-xv-line mb-4 overflow-hidden">
                <div
                    className="h-full bg-xv-gold transition-all duration-700"
                    style={{ width: `${pct}%` }}
                />
            </div>
            {job.current_slug && !done && !failed && (
                <p className="text-[13px] text-xv-text-secondary font-light italic">
                    Article en cours : <span className="text-xv-black">{job.current_slug}</span>
                </p>
            )}
            {done && (
                <p className="text-[13px] text-xv-black font-light">
                    {job.succeeded}/{job.total} article{job.total > 1 ? "s" : ""} enrichi{job.succeeded > 1 ? "s" : ""} avec succès.
                </p>
            )}
            {failed && job.error && (
                <p className="text-[13px] text-red-700 font-light">
                    Erreur : {job.error}
                </p>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// SocialAITab — génération IA LinkedIn / Instagram
// ─────────────────────────────────────────────────────────────

const TONE_OPTIONS = [
    { v: "institutionnel", l: "Institutionnel" },
    { v: "editorial", l: "Éditorial" },
    { v: "reflexif", l: "Réflexif" },
    { v: "inspirant", l: "Inspirant sobre" },
    { v: "experts", l: "Expert technique" },
    { v: "discret", l: "Confidentiel · 3 phrases" },
];

function SocialAITab() {
    const [topic, setTopic] = useState("");
    const [platform, setPlatform] = useState("linkedin");
    const [tone, setTone] = useState("institutionnel");
    const [count, setCount] = useState(3);
    const [context, setContext] = useState("");
    const [busy, setBusy] = useState(false);
    const [posts, setPosts] = useState([]);
    const [history, setHistory] = useState([]);
    const [err, setErr] = useState("");
    const [copiedId, setCopiedId] = useState(null);

    const loadHistory = () =>
        api
            .get("/admin/social/history?limit=10")
            .then(({ data }) => setHistory(data))
            .catch(() => setHistory([]));

    useEffect(() => {
        loadHistory();
    }, []);

    const generate = async (e) => {
        e?.preventDefault();
        if (!topic.trim()) return;
        setBusy(true);
        setErr("");
        setPosts([]);
        try {
            const { data } = await api.post(
                "/admin/social/generate",
                { topic, platform, tone, count, context },
                { timeout: 90000 },
            );
            setPosts(data.posts || []);
            await loadHistory();
        } catch (e) {
            setErr(
                e?.response?.data?.detail ||
                    "Génération impossible. Réessayez dans un instant.",
            );
        } finally {
            setBusy(false);
        }
    };

    const formatPost = (p) => {
        const tags = (p.hashtags || [])
            .map((t) => (t.startsWith("#") ? t : `#${t}`))
            .join(" ");
        return tags ? `${p.text}\n\n${tags}` : p.text;
    };

    const copy = async (p) => {
        try {
            await navigator.clipboard.writeText(formatPost(p));
            setCopiedId(p.id);
            setTimeout(() => setCopiedId(null), 1500);
        } catch {
            /* noop */
        }
    };

    const removeHistory = async (id) => {
        if (!window.confirm("Supprimer cette génération de l'historique ?")) return;
        await api.delete(`/admin/social/history/${id}`);
        await loadHistory();
    };

    return (
        <section>
            <div className="mb-10">
                <div className="overline text-xv-gold mb-2">
                    <Sparkles
                        strokeWidth={1.5}
                        size={14}
                        className="inline-block mr-2 align-middle -mt-1"
                    />
                    IA éditoriale
                </div>
                <h2
                    className="font-serif text-4xl md:text-5xl text-xv-black"
                    style={{ fontWeight: 400 }}
                >
                    Suggestions LinkedIn &amp; Instagram
                </h2>
                <p className="mt-3 text-xv-text-secondary font-light text-[15px] max-w-2xl">
                    Génération de propositions éditoriales conformes à la voix
                    du cabinet — vocabulaire AMF strict, ton institutionnel.
                    Aucune publication automatique : vous validez et copiez.
                </p>
            </div>

            <form
                onSubmit={generate}
                data-testid="social-form"
                className="bg-white border border-xv-line p-8 mb-10"
            >
                <div className="grid md:grid-cols-4 gap-6 mb-6">
                    <div className="md:col-span-2">
                        <label className="block overline text-xv-gold mb-3">
                            Sujet de la publication
                        </label>
                        <input
                            type="text"
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder="Ex. La discrétion comme actif stratégique en transmission"
                            required
                            data-testid="social-topic"
                            className="w-full bg-transparent border-b border-xv-line focus:border-xv-gold outline-none py-2 text-[15px] font-serif text-xv-black"
                        />
                    </div>
                    <div>
                        <label className="block overline text-xv-gold mb-3">
                            Plateforme
                        </label>
                        <select
                            value={platform}
                            onChange={(e) => setPlatform(e.target.value)}
                            data-testid="social-platform"
                            className="w-full bg-transparent border-b border-xv-line focus:border-xv-gold outline-none py-2 text-[15px] font-serif text-xv-black"
                        >
                            <option value="linkedin">LinkedIn</option>
                            <option value="instagram">Instagram</option>
                        </select>
                    </div>
                    <div>
                        <label className="block overline text-xv-gold mb-3">
                            Nombre
                        </label>
                        <select
                            value={count}
                            onChange={(e) => setCount(Number(e.target.value))}
                            data-testid="social-count"
                            className="w-full bg-transparent border-b border-xv-line focus:border-xv-gold outline-none py-2 text-[15px] font-serif text-xv-black"
                        >
                            {[1, 2, 3, 4, 5].map((n) => (
                                <option key={n} value={n}>
                                    {n} proposition{n > 1 ? "s" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                    <div className="md:col-span-3">
                        <label className="block overline text-xv-gold mb-3">
                            Ton éditorial
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {TONE_OPTIONS.map((t) => (
                                <button
                                    key={t.v}
                                    type="button"
                                    data-testid={`social-tone-${t.v}`}
                                    onClick={() => setTone(t.v)}
                                    className={`text-[11px] tracking-[0.18em] uppercase px-4 py-2 border transition-all duration-300 ${
                                        tone === t.v
                                            ? "border-xv-midnight bg-xv-midnight text-xv-ivory"
                                            : "border-xv-line text-xv-text-secondary hover:border-xv-midnight hover:text-xv-midnight"
                                    }`}
                                >
                                    {t.l}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="mb-6">
                    <label className="block overline text-xv-gold mb-3">
                        Contexte complémentaire (optionnel)
                    </label>
                    <textarea
                        value={context}
                        onChange={(e) => setContext(e.target.value)}
                        rows={3}
                        placeholder="Détails, actualité à mentionner, angle particulier souhaité…"
                        data-testid="social-context"
                        className="w-full bg-transparent border border-xv-line focus:border-xv-gold outline-none p-3 text-[14px] leading-[1.7] font-serif text-xv-black"
                    />
                </div>
                <div className="flex justify-end items-center gap-4">
                    {err && (
                        <span
                            data-testid="social-error"
                            className="text-[12px] text-red-700 italic"
                        >
                            {err}
                        </span>
                    )}
                    <button
                        type="submit"
                        disabled={busy || !topic.trim()}
                        data-testid="social-generate"
                        className="inline-flex items-center gap-3 px-6 py-3 bg-xv-midnight text-xv-ivory text-[11px] tracking-[0.28em] uppercase hover:bg-xv-black transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Sparkles strokeWidth={1.5} size={14} />
                        {busy ? "Génération…" : "Générer les propositions"}
                    </button>
                </div>
            </form>

            {posts.length > 0 && (
                <div
                    data-testid="social-posts"
                    className="space-y-8 mb-12"
                >
                    {posts.map((p, idx) => (
                        <article
                            key={p.id}
                            data-testid={`social-post-${idx}`}
                            className="bg-white border border-xv-line p-8 relative"
                        >
                            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                                <div className="overline text-xv-gold">
                                    Proposition {idx + 1} · {p.platform} ·{" "}
                                    {p.tone}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => copy(p)}
                                    data-testid={`social-copy-${idx}`}
                                    className={`text-[10px] tracking-[0.28em] uppercase px-4 py-2 border transition-all duration-300 ${
                                        copiedId === p.id
                                            ? "border-xv-gold bg-xv-gold/10 text-xv-gold"
                                            : "border-xv-midnight text-xv-midnight hover:bg-xv-midnight hover:text-xv-ivory"
                                    }`}
                                >
                                    {copiedId === p.id ? "Copié ✓" : "Copier"}
                                </button>
                            </div>
                            {p.hook && (
                                <p className="font-serif text-[20px] md:text-[22px] text-xv-black leading-snug mb-5">
                                    {p.hook}
                                </p>
                            )}
                            <p className="text-[15px] leading-[1.85] text-xv-text-secondary font-light whitespace-pre-line">
                                {p.text}
                            </p>
                            {p.hashtags?.length > 0 && (
                                <div className="mt-5 pt-5 border-t border-xv-line/60 flex flex-wrap gap-2">
                                    {p.hashtags.map((h, i) => (
                                        <span
                                            key={i}
                                            className="text-[12px] tracking-wider text-xv-gold/85 font-serif"
                                        >
                                            #{h}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            )}

            {history.length > 0 && (
                <div data-testid="social-history">
                    <div className="overline text-xv-gold mb-5">
                        Historique des générations
                    </div>
                    <ul className="border-t border-xv-line">
                        {history.map((h) => (
                            <li
                                key={h.id}
                                className="grid grid-cols-[auto_1fr_auto_auto] gap-5 py-4 border-b border-xv-line items-center"
                            >
                                <span className="text-[10px] tracking-[0.28em] uppercase text-xv-gold w-[80px]">
                                    {h.platform}
                                </span>
                                <div className="min-w-0">
                                    <p className="font-serif text-[15px] text-xv-black truncate">
                                        {h.topic}
                                    </p>
                                    <p className="text-[11px] text-xv-text-secondary/70 italic mt-1">
                                        {h.tone} · {(h.posts || []).length}{" "}
                                        proposition{(h.posts || []).length > 1 ? "s" : ""}
                                    </p>
                                </div>
                                <span className="text-[11px] text-xv-text-secondary/65 whitespace-nowrap">
                                    {new Date(h.created_at).toLocaleString("fr-FR", {
                                        day: "2-digit",
                                        month: "short",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setPosts(h.posts || [])}
                                        className="text-[10px] tracking-[0.24em] uppercase text-xv-midnight hover:text-xv-gold"
                                    >
                                        Rouvrir
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => removeHistory(h.id)}
                                        className="text-xv-text-secondary/40 hover:text-red-700"
                                    >
                                        <Trash2 strokeWidth={1.3} size={13} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

export { AdminShell };
